import React, { useRef, useState, useContext, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import moment from "moment";

// PDFMake
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

// OL
import { transform as OlTransform } from 'ol/proj';
import { getDistance as OlGetDistance } from 'ol/sphere';

// Context
import MapContext, { MapContextType } from "@/context/MapContext/MapContext"
import SnackbarContext from "@/context/SnackbarContext/SnackbarContext";
import TenantContext, { TenantContextType } from "@/context/TenantContext/TenantContext";
import UserContext from "@/context/UserContext/UserContext";

// Components
import { formats } from '@/lib/formats';
import mapService from '@/services/mapService';
import useStateWithLS from '@/components//useStateWithLS';

import mapLogo from '@/svgs/map_logo.svg';
import prehnitLogo from '@/svgs/prehnit.svg';

// Types
export type PaperFormat = "A3" | "A4";
export type PaperOrientation = "portrait" | "landscape";
export type FileFormat = "pdf" | "png";
export type PrintScale = 100 | 200 | 500 | 1000 | 2000 | 2500 | 5000 | 10000 | 25000;

export type FrameDimensions = {
  widthPx: number,
  heightPx: number,
  paperWidthPx: number,
  paperHeightPx: number
}

type PrintDimensions = {
  paperWidthMm: number,
  paperHeightMm: number,
  mapWidthMm: number,
  mapHeightMm: number
}

// PDFMake
pdfMake.vfs = pdfFonts.pdfMake.vfs;

// A4: 210 x 297 mm
// A3: 297 x 420 mm

const printSizes = {
  pdf: {
    A4: { paperWidthMm: 210, paperHeightMm: 297, mapWidthMm: 180, mapHeightMm: 267 } as PrintDimensions,
    A3: { paperWidthMm: 297, paperHeightMm: 420, mapWidthMm: 267, mapHeightMm: 390 } as PrintDimensions
  },
  png: {
    A4: { paperWidthMm: 210, paperHeightMm: 297, mapWidthMm: 180, mapHeightMm: 267 } as PrintDimensions,
    A3: { paperWidthMm: 297, paperHeightMm: 420, mapWidthMm: 267, mapHeightMm: 390 } as PrintDimensions
  }
};

const printScales: Array<PrintScale> = [ 100, 200, 500, 1000, 2000, 2500, 5000, 10000, 25000];

export { printScales };

type UseMapPrintProps = {
  mapId: number
}

export interface IMapPrint {
  paperFormat: PaperFormat;
  setPaperFormat: React.Dispatch<React.SetStateAction<PaperFormat>>;

  paperOrientation: PaperOrientation;
  setPaperOrientation: React.Dispatch<React.SetStateAction<PaperOrientation>>;

  fileFormat: FileFormat;
  setFileFormat: React.Dispatch<React.SetStateAction<FileFormat>>;

  frameDimensions: FrameDimensions;

  printScale: PrintScale,
  setPrintScaleManually: (s: PrintScale | null) => void;

  downloadMap: () => void;
}

// Hook
function useMapPrint(props: UseMapPrintProps): IMapPrint {
  const mapContext = useContext<MapContextType | null>(MapContext);
  const snackbarContext = useContext(SnackbarContext);
  const tenantContext = useContext(TenantContext) as TenantContextType;
  const userContext = useContext(UserContext);

  const isDraggingRef = useRef(false);
  const isZoomingRef = useRef(false);

  const { t } = useTranslation();

  const [paperFormat, setPaperFormat] = useStateWithLS<PaperFormat>('napprint_paperformat', "A4");
  const [paperOrientation, setPaperOrientation] = useStateWithLS<PaperOrientation>('mapprint_paperorinentation', "landscape");
  const [fileFormat, setFileFormat] = useStateWithLS<FileFormat>('mapprint_fileformat', "pdf");
  const [printScale, setPrintScale] = useStateWithLS<PrintScale>('mapprint_printscale', 2000);

  const [frameDimensions, setFrameDimensions] = useState<FrameDimensions>({ widthPx: 0, heightPx: 0, paperWidthPx: 0, paperHeightPx: 0 });

  const { mapId } = props;

  // const ppi = 96; // pixels per inch

  const rotatePrintDimensions = (ps: PrintDimensions): PrintDimensions => 
    // const a = 0;
     ({
      paperWidthMm: ps.paperHeightMm,
      paperHeightMm: ps.paperWidthMm,
      mapWidthMm: ps.mapHeightMm,
      mapHeightMm: ps.mapWidthMm
    })
  

  const getCorrectPrintSize = () => {
    const printSize = printSizes[fileFormat][paperFormat];

    return paperOrientation === "landscape" ? rotatePrintDimensions(printSize) : printSize;
  }

  const printSize = getCorrectPrintSize();

  const recalcFrameDimensions = (allowPrintScaleChange: boolean = false) => {

    if (!mapContext?.map) return;

    const mapSizePx = mapContext.map.getSize();
    if (!mapSizePx) return;
    const [mapWidthPx, mapHeightPx] = mapSizePx;

    const mapResolution = getMapResolution(); // 1px is x meters
    if (!mapResolution) return;

    const printMapWidthMm = printSize.mapWidthMm;
    const printMapHeightMm = printSize.mapHeightMm;

    const worldMapWidthM = printMapWidthMm * printScale / 1000;
    const worldMapHeightM = printMapHeightMm * printScale / 1000;

    const frameMapWidthPx = worldMapWidthM / mapResolution;
    const frameMapHeightPx = worldMapHeightM / mapResolution;

    const framePaperWidthPx = printSize.paperWidthMm * printScale / 1000 / mapResolution;
    const framePaperHeightPx = printSize.paperHeightMm * printScale / 1000 / mapResolution;

    if ((framePaperWidthPx > mapWidthPx) || (framePaperHeightPx > mapHeightPx)) {
      if (allowPrintScaleChange) {
        const currentPrintScaleIdnex = printScales.findIndex(x => x === printScale);
        if (currentPrintScaleIdnex > 1) {
          const newPrintScale = printScales[currentPrintScaleIdnex - 1];
          setPrintScale(newPrintScale);
        }
      } else {
        // zoom map
        const view = mapContext.map.getView();
        const currentZoom = view.getZoom();
        if (currentZoom) {
          view.setZoom(currentZoom - 1);
        }
      }

    }
    
    setFrameDimensions({ widthPx: frameMapWidthPx, heightPx: frameMapHeightPx, paperWidthPx: framePaperWidthPx, paperHeightPx: framePaperHeightPx });
    // const scaleFactor = Math.min(mapWidthPx * 0.6 / printSize.mapWidthMm, mapHeightPx * 0.6 / printSize.mapHeightMm);

    // const frameMapWidthPx = printSize.mapWidthMm * scaleFactor;
    // const frameMapHeightPx = printSize.mapHeightMm * scaleFactor;

    // const framePaperWidthPx = printSize.paperWidthMm * scaleFactor;
    // const framePaperHeightPx = printSize.paperHeightMm * scaleFactor;
  }

  useEffect(() => {
    recalcFrameDimensions();
  }, [])

  useEffect(() => {
    const handleResize = () => {
      recalcFrameDimensions();
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [paperFormat, paperOrientation]);

  // It could be interesting to track when we drag map and when we zoom in/out
  useEffect(() => {
    const onPointerDrag = () => {
      isDraggingRef.current = true;
    };

    const onResolutionChange = () => {
      isZoomingRef.current = true;
    };

    if (mapContext?.map) {
      mapContext.map.on('pointerdrag', onPointerDrag);
      mapContext.map.getView().on('change:resolution', onResolutionChange);
    }

    return () => {
      if (mapContext?.map) {
        mapContext.map.un('pointerdrag', onPointerDrag);
        mapContext.map.getView().un('change:resolution', onResolutionChange);
      }
    };
  }, [mapContext?.map]);

  // When we move a map, we need to do some calculations
  useEffect(() => {
    const handleMoveEnd = () => {
      // recalcPrintScale();
      if (isZoomingRef.current) {
        recalcFrameDimensions(true);
      } else {
        recalcFrameDimensions();
      }

      isDraggingRef.current = false;
      isZoomingRef.current = false;
    };

    if (mapContext?.map) {
      mapContext.map.on('moveend', handleMoveEnd);
    }
    return () => {
      if (mapContext?.map) {
        mapContext.map.un('moveend', handleMoveEnd);
      }
    };
  }, [mapContext?.map, fileFormat, paperFormat, paperOrientation, frameDimensions]);

  // on change of FileFormat, PaperFormat and PaperOrientation, we will recalculate Frame Dimensions
  useEffect(() => {
    recalcFrameDimensions();
  }, [fileFormat, paperFormat, paperOrientation])

  // on change of FrameDimensions, we will recalculate printScale
  // useEffect(() => {
  //   recalcPrintScale();
  // }, [frameDimensions])

  useEffect(() => {
    recalcFrameDimensions();
  }, [printScale])

  const setPrintScaleManually = (s: PrintScale | null) => {

    // console.log('setting new print scale: ', s);

    
    const newPrintScale = s || 2000; // 1 cm on map is 2000 cm in world
    
    setPrintScale(newPrintScale);


    // const printMapWidthMm = printSize.mapWidthMm;
    // const worldCoverageMm = printMapWidthMm * newPrintScale;
    // const worldCoverageM = worldCoverageMm / 1000; // this should fit in Frame defined with frameDimensions

    // // calculate needed scale on map
    // const frameWidthPx = frameDimensions.widthPx;

    // const neededResolution = worldCoverageM / frameWidthPx;

    // if (mapContext?.map && newPrintScale) {
    //   const view = mapContext.map.getView();

    //   view.setResolution(neededResolution);
    // }
  }

  // const recalcPrintScale = (): void => {
  //   if (mapContext?.map) {

  //     const mapResolution = getMapResolution();
  //     if (!mapResolution) return;

  //     const frameWidthPx = frameDimensions.widthPx;

  //     const worldCoverageWidthM = frameWidthPx * mapResolution;
  //     const worldCoverageWidthMm = worldCoverageWidthM * 1000;

  //     const printMapWidthMm = printSize.mapWidthMm;

  //     const newPrintScale = worldCoverageWidthMm / printMapWidthMm;

  //     setPrintScale(newPrintScale);
  //   }
  // };

  const formatPrintScale = (d: number): string => {
    if (!d) {
      return '1:???';
    }

    if (d > 100) {
      const s = Math.round(d / 100) * 100;
      return `1:${s.toString()}`;
    }

    return `1:${Math.round(d).toString()}`;
  };

  // const resolution2scale = (resolution: number, dpi: number = 96) => resolution * dpi * (1 / 0.0254);

  // const scale2resolution = (scale: number, dpi: number = 96) => scale * 0.0254 / dpi;

  // Returns how many World meters are in 1 pixel
  const getMapResolution = () => {
    const map = mapContext?.map;
    if (!map) return null;

    const view = map.getView();
    const proj = view.getProjection();
    const center = view.getCenter();

    if (!center) return null;

    const pA = map.getPixelFromCoordinate(center);

    if (!pA) return null;

    const pB = [pA[0], pA[1] + 1]; // we move point one pixel on a map

    const coord = map.getCoordinateFromPixel(pB);

    const res = OlGetDistance(
      OlTransform(center, proj, 'EPSG:4326'),
      OlTransform(coord, proj, 'EPSG:4326')
    );

    return res;
  }

  const downloadMap = () => {
    const frameRect = document.querySelector('.frame')?.getBoundingClientRect();

    if (!frameRect) return;

    switch (fileFormat) {
      case "png":
        downloadMapAsImage('print', frameRect)
        break;
      case "pdf":
        downloadMapAsPDF('print', frameRect)
        break;
      default:
      // do nothing
    }
  }

  const generateImageSection = (
    frameRect: DOMRect
  ) => new Promise((resolve) => {
    const map = mapContext?.map;

    if (map === null || map === undefined) return;

    map.once('rendercomplete', () => {
      const fullCanvas = document.createElement('canvas');
      const size = map.getSize();
      if (size) {
        [fullCanvas.width, fullCanvas.height] = size;
      }
      const fullCtx = fullCanvas.getContext('2d');
      if (fullCtx === null) return;

      Array.prototype.forEach.call(
        map.getViewport().querySelectorAll('.ol-layer canvas, canvas.ol-layer'),
        (canvas) => {
          if (canvas.width > 0) {
            const opacity = canvas.parentNode.style.opacity || canvas.style.opacity;
            fullCtx.globalAlpha = opacity === '' ? 1 : Number(opacity);
            let matrix;
            const { transform } = canvas.style;
            if (transform) {
              matrix = transform
                .match(/^matrix\(([^(]*)\)$/)[1]
                .split(',')
                .map(Number);
            } else {
              matrix = [
                parseFloat(canvas.style.width) / canvas.width,
                0,
                0,
                parseFloat(canvas.style.height) / canvas.height,
                0,
                0,
              ];
            }
            CanvasRenderingContext2D.prototype.setTransform.apply(fullCtx, matrix);
            const { backgroundColor } = canvas.parentNode.style;
            if (backgroundColor) {
              fullCtx.fillStyle = backgroundColor;
              fullCtx.fillRect(0, 0, canvas.width, canvas.height);
            }
            fullCtx.drawImage(canvas, 0, 0);
          }
        }
      );
      fullCtx.globalAlpha = 1;
      fullCtx.setTransform(1, 0, 0, 1, 0, 0);

      const exportCanvas = fullCanvas;

      const ctx = exportCanvas.getContext("2d");
      const mapContainer = map.getTargetElement().getBoundingClientRect();

      const sx = frameRect.x - mapContainer.x;
      const sy = frameRect.y - mapContainer.y;

      if (ctx) {
        drawScaleBox(ctx, exportCanvas, sx, sy, frameRect);
        drawNorthArrow(ctx, sx, sy, frameRect);
      }

      const pctx = exportCanvas.getContext("2d")!;
      // top
      pctx.clearRect(0, 0, exportCanvas.width, sy);
      // left
      pctx.clearRect(0, 0, sx, exportCanvas.height);
      // bottom
      pctx.clearRect(0, sy + frameRect.height, exportCanvas.width, exportCanvas.height);
      // right
      pctx.clearRect(sx + frameRect.width, 0, exportCanvas.width, exportCanvas.height);

      const cropped = cropImageFromCanvas(pctx)
      if (cropped) {
        if (fileFormat === "pdf") {
          const image = cropped.toDataURL("image/png");
          const imageWidthPt = printSize.mapWidthMm * 72 / 25.4;
          const imageHeightPt = printSize.mapHeightMm * 72 / 25.4;
          const imageSection = {
            image,
            alignment: 'center',
            width: imageWidthPt,
            height: imageHeightPt,
            margin: [0, 0, 0, 0]
          }
          resolve(imageSection)
        } else {
          const image = cropped.toDataURL(`image/${fileFormat}`);
          resolve(image);
        }
      }
    });
    map.renderSync();
  });

  const checkIfBaseMapIsDownloadable = (): boolean => {
    const map = mapContext?.map;

    if (map === null || map === undefined) return false;

    // @ts-ignore
    const baseLayerArray = map.getLayers().getArray()[0].getLayers().getArray()

    const activeBaseLayer = baseLayerArray.find((layer: any) => layer.getVisible() === true);

    if (activeBaseLayer) {
      const allowPrint = activeBaseLayer.get('allow_print');
  
      if (!allowPrint) {
        snackbarContext.showNotification('map:map_print_not_allowed', "error");
        return false;
      }
    }


    return true;
  }

  const downloadMapAsImage = (filename: string, frameRect: DOMRect) => {
    if (!checkIfBaseMapIsDownloadable()) return;

    generateImageSection(frameRect)
      .then((imageDataUrl) => {

        const link = document.createElement('a');
        link.href = imageDataUrl as any;
        link.download = `${filename}.png`;
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
      })
      .catch((error) => {
        console.error('Error generating or downloading the map image:', error);
      });
  }

  const downloadMapAsPDF = async (filename: string, frameRect: DOMRect) => {
    if (!checkIfBaseMapIsDownloadable()) return;

    const config = buildExportConfig();

    const  { qrCode, urlHash} = await mapService.storePrintConfig(config);
    if (!qrCode) {
      snackbarContext.showNotification(t('map:map_print_error'), "error");
      return;
    }

    const [mapLogoSvg, prehnitLogoSvg] = await Promise.all([
      fetch(mapLogo).then(r => r.text()),
      fetch(prehnitLogo).then(r => r.text()),
    ]);


    const [image, header, footer] = await Promise.all([
      generateImageSection(frameRect),
      generatePdfHeader(),
      generatePdfFooter(mapLogoSvg, prehnitLogoSvg, qrCode, urlHash),
    ]);

    const documentDefinition = {
      pageSize: paperFormat,
      pageOrientation: paperOrientation,
      header,
      footer,
      content: [image]
    };

    // @ts-ignore
    pdfMake.createPdf(documentDefinition).download(filename);
  }

  // Drawing functions

  const drawScaleBox = (
    ctx: CanvasRenderingContext2D,
    exportCanvas: HTMLCanvasElement,
    sx: number,
    sy: number,
    frameRect: DOMRect
  ) => {
    ctx.font = paperOrientation === "landscape" ? "14px Arial" : paperFormat === "A4" ? "10px Arial" : "11px Arial";
    ctx.textAlign = "right";
    ctx.textBaseline = "bottom";

    const printScaleFormatted = formatPrintScale(printScale);

    const padding = 4;

    const metrics = ctx.measureText(printScaleFormatted);
    const textWidth = metrics.width;
    const textHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

    const x = exportCanvas.width - (exportCanvas.width - sx - frameRect.width) - 10;
    const y = sy + frameRect.height - 10;

    const rectX = x - textWidth - padding;
    const rectY = y - metrics.actualBoundingBoxAscent - padding;
    const rectWidth = textWidth + padding * 2;
    const rectHeight = textHeight + padding * 2;
    const borderRadius = 4;

    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.beginPath();
    ctx.moveTo(rectX + borderRadius, rectY);
    ctx.lineTo(rectX + rectWidth - borderRadius, rectY);
    ctx.quadraticCurveTo(rectX + rectWidth, rectY, rectX + rectWidth, rectY + borderRadius);
    ctx.lineTo(rectX + rectWidth, rectY + rectHeight - borderRadius);
    ctx.quadraticCurveTo(rectX + rectWidth, rectY + rectHeight, rectX + rectWidth - borderRadius, rectY + rectHeight);
    ctx.lineTo(rectX + borderRadius, rectY + rectHeight);
    ctx.quadraticCurveTo(rectX, rectY + rectHeight, rectX, rectY + rectHeight - borderRadius);
    ctx.lineTo(rectX, rectY + borderRadius);
    ctx.quadraticCurveTo(rectX, rectY, rectX + borderRadius, rectY);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = "black";
    ctx.fillText(printScaleFormatted, x, y);
  }

  const drawNorthArrow = (
    ctx: CanvasRenderingContext2D,
    sx: number,
    sy: number,
    frameRect: DOMRect
  ) => {
    const rotation = mapContext?.map?.getView().getRotation() ?? 0;

    const anchorX = frameRect.width / 2 + sx;
    const anchorY = 20 + sy;
    const spacing = 15;

    ctx.font = "bold 10px Arial";
    ctx.fillStyle = "black";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText(t("map:map_print_sjever"), anchorX, anchorY);

    ctx.save();

    const scale = 0.7;

    const upperArrow = [
      [17.72, 0],
      [12.948, -14.316],
      [11.052, -14.316],
      [6.28, 0],
    ];
    const lowerArrow = [
      [15.613, 2],
      [12, 12.838],
      [8.387, 2],
      [6.28, 2],
      [11.052, 16.316],
      [12.948, 16.316],
      [17.72, 2],
    ];

    const allPoints = [...upperArrow, ...lowerArrow];

    let sumX = 0;
    let sumY = 0;
    allPoints.forEach(([x, y]) => {
      sumX += x;
      sumY += y;
    });
    const centerX = sumX / allPoints.length;
    const centerY = sumY / allPoints.length;

    ctx.translate(anchorX, anchorY + spacing);

    ctx.rotate(rotation);

    ctx.translate(-centerX * scale, -centerY * scale);

    ctx.beginPath();
    ctx.moveTo(upperArrow[0][0] * scale, upperArrow[0][1] * scale);
    for (let i = 1; i < upperArrow.length; i += 1) {
      ctx.lineTo(upperArrow[i][0] * scale, upperArrow[i][1] * scale);
    }
    ctx.closePath();
    ctx.fillStyle = "black";
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(lowerArrow[0][0] * scale, lowerArrow[0][1] * scale);
    for (let i = 1; i < lowerArrow.length; i += 1) {
      ctx.lineTo(lowerArrow[i][0] * scale, lowerArrow[i][1] * scale);
    }
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  };

  const cropImageFromCanvas = (ctx: CanvasRenderingContext2D) => {
    const { canvas } = ctx;
    const w = canvas.width;
    const h = canvas.height;
    const imageData = ctx.getImageData(0, 0, w, h);

    const xCoords: number[] = [];
    const yCoords: number[] = [];

    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        const index = (y * w + x) * 4;
        const alpha = imageData.data[index + 3];
        if (alpha > 0) {
          xCoords.push(x);
          yCoords.push(y);
        }
      }
    }

    if (xCoords.length === 0 || yCoords.length === 0) {
      return undefined;
    }

    xCoords.sort((a, b) => a - b);
    yCoords.sort((a, b) => a - b);

    const minX = xCoords[0];
    const maxX = xCoords[xCoords.length - 1];
    const minY = yCoords[0];
    const maxY = yCoords[yCoords.length - 1];

    const newWidth = maxX - minX + 1;
    const newHeight = maxY - minY + 1;

    const cut = ctx.getImageData(minX, minY, newWidth, newHeight);

    canvas.width = newWidth;
    canvas.height = newHeight;
    ctx.putImageData(cut, 0, 0);

    return canvas;
  }

  // PDF Functions
  async function generatePdfHeader() {

    const crest = tenantContext?.tenantTheme?.crest;

    const crestBase64 = `data:image/png;base64,${crest}`;
    const timestamp = moment
      .utc(new Date())
      .local()
      .format(formats.datetime);

    let fontSize = 14;
    const title = t('app_title');
    if (title.length > 40) {
      const scaleFactor = 40 / title.length * 1.6;
      fontSize = Math.max(6, Math.floor(fontSize * scaleFactor));
    }

    return {
      columns: [
        {
          image: crestBase64,
          maxHeight: 30,
          maxWidth: 60,
          width: 60
        },
        {
          text: title,
          alignment: 'center',
          fontSize,
          bold: true,
          width: '*',
          height: 40,
          margin: [0, 8, 0, 0], // left, top, right, bottom
        },
        {
          columns: [
            { width: '*', text: '' },
            {
              width: 'auto',
              stack: [
                { text: `${t('map:map_print_datum')  }:`, fontSize: 8, margin: [0, 5, 0, 2], alignment: 'left' },
                { text: timestamp, fontSize: 8, alignment: 'left' }
              ],
              margin: [0, 0, 0, 0] // left, top, right, bottom
            }
          ],
          width: 80,
          alignment: 'right'
        }
      ],
      margin: [40, 5, 40, 0], // left, top, right, bottom
    };
  }

  async function generatePdfFooter(
    map_logo: string,
    prehnit_logo: string,
    qr_code: string,
    urlHash: string,
  ) {

    return {
      columns: [
        { svg: map_logo, width: 40, margin: [0, 15, 0, 0] }, // left, top, right, bottom
        { text: '', width: '*'},
        {
          stack: [
            { text: `${t('map:map_print_uputa1')}:`, fontSize: 8, margin: [0, 5, 0, 1] }, // left, top, right, bottom
            { text: `a) ${t('map:map_print_uputa2')}`, fontSize: 8, margin: [8, 0, 0, 1] },
            { text: `b) ${t('map:map_print_uputa3')} ${window.location.origin}/map#print`, fontSize: 8, margin: [8, 0, 0, 0] },
          ],
          width: 'auto'
        },
        { image: qr_code, width: 40 },
        {
          table: {
            body: [
              [{ text: urlHash.toUpperCase(), fontSize: 9 }]
            ]
          },
          layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => 'black',
            vLineColor: () => 'black'
          },
          margin: [5, 13, 0, 0], // left, top, right, bottom
        },
        { text: '', width: '*'},
        { svg: prehnit_logo, width: 30, alignment: 'right', margin: [0, 5, 0, 0] } // left, top, right, bottom
      ],
      margin: [40, 0, 40, 0], // left, top, right, bottom
    };
  }

  function buildExportConfig() {
    const maplayers = localStorage.getItem("maplayers");
    const mapbaselayers = localStorage.getItem("mapbaselayers");
    const key = `viewsState_${userContext?.username}`;

    const viewsStateRaw = localStorage.getItem(key);
    let viewsState = null;
    if (viewsStateRaw) {
      try {
        const parsed = JSON.parse(viewsStateRaw);
        const currentIndex = parsed.currentViewIndex ? parsed.currentViewIndex - 1 : 0;

        viewsState = {
          viewSwitchClicked: parsed.viewSwitchClicked,
          views: parsed.views ? [parsed.views[currentIndex]] : [],
        };
      } catch (e) {
        console.error("Error parsing viewsState:", e);
      }
    }
    return {
      viewsState: JSON.stringify(viewsState),
      maplayers,
      mapbaselayers,
      mapId
    };
  }

  return {
    paperFormat,
    setPaperFormat,

    paperOrientation,
    setPaperOrientation,

    fileFormat,
    setFileFormat,

    frameDimensions,

    printScale,
    setPrintScaleManually,

    downloadMap
  }
}

export default useMapPrint;
